import './App.css';
import React, { useEffect, useState } from 'react';
import './assets/css/style.css'

import { isReduxStoredCleared } from './storage';
import { useCustomDispatch, useCustomNavigate, useVersionCheck } from './hooks';

import { useAuthSelector } from './services/selectors/authSelector';
import { authToken, clearAuthToken } from './storage/authToken';
import { fetchPharmacyDetails } from './services/apis/dispense/getPharmacyDetail';

import AppRoutes from './routes/appRoutes';
import DoseGapOverrideModal from './components/common/DoseGapOverrideModal';

import Ably from 'ably';
import * as routeNames from './routes/routeName';
import * as EndPoints from './networking/urlEndPoints'
import { subscribeToAblyChannel } from './utils/ablySubscription';

function App() {

	const { pharmacyId } = useAuthSelector()
	const navigate = useCustomNavigate();
	const dispatch = useCustomDispatch();
	const { updateAvailable, reloadPage } = useVersionCheck()
	const [handoverResponse, setHandoverResponse] = useState(null);

	const token = authToken()

	useEffect(() => {
		if (isReduxStoredCleared) {
			clearAuthToken()
			navigate(routeNames.HOME_ROUTE)
		}
	}, [])

	const [isOnline, setIsOnline] = useState(window.navigator.onLine);

	useEffect(() => {
		const handleOnlineStatusChange = () => {
			setIsOnline(window.navigator.onLine);
		};

		window.addEventListener('online', handleOnlineStatusChange);
		window.addEventListener('offline', handleOnlineStatusChange);

		return () => {
			window.removeEventListener('online', handleOnlineStatusChange);
			window.removeEventListener('offline', handleOnlineStatusChange);
		};
	}, []);

	useEffect(() => {

		const ably = new Ably.Realtime({ key: EndPoints.apiKey });
		const channel = ably.channels.get('oat-pharmacy-inspection-mode');

		const eventName = `inspection-${pharmacyId}`;
		const handleInspectionUpdate = (message) => {
			if (message.name === `inspection-${pharmacyId}` && pharmacyId && token) {
				dispatch(fetchPharmacyDetails(pharmacyId))
			}
		};
		const unsubscribe = subscribeToAblyChannel({ channel, eventName, listener: handleInspectionUpdate });

		return () => {
			unsubscribe();
			if (ably.connection.state === 'connected') {
				ably.close();
			}
		}
	}, [pharmacyId]);

	useEffect(() => {
		if (!pharmacyId) return;

		const ably = new Ably.Realtime({ key: EndPoints.apiKey });
		const channel = ably.channels.get('pharmacy-notifications');
		const eventName = `pharmacy-${pharmacyId}`;

		const handleHandoverResponse = (message) => {
			const payload = message?.data;
			const isInstoreHandoverResponse = payload?.type === 'delivery_handover_response'
				&& payload?.handover_type === 'instore_unassigned'
				&& ['accepted', 'rejected'].includes(payload?.status);
			if (!isInstoreHandoverResponse) {
				return;
			}

			setHandoverResponse({
				status: payload.status,
				recipientName: payload?.responded_by?.name || payload?.target_name || 'The recipient',
			});
		};

		const unsubscribe = subscribeToAblyChannel({ channel, eventName, listener: handleHandoverResponse });

		return () => {
			unsubscribe();
			ably.close();
		};
	}, [pharmacyId]);

	useEffect(() => {
		window.addEventListener('scroll', () => {
			const metaTag = document.querySelector('meta[name="theme-color"]');
			if (window.scrollY / window.innerHeight > 0.8) {
				metaTag.setAttribute("content", "#FDF8F7");
			} else {
				metaTag.setAttribute("content", "#FDF8F7");
			}
		}, false)
	}, [pharmacyId])

	return (
		<>
			{/* Mounted once: every delivery screen raises the early-dose
			    confirmation through the same prompt. */}
			<DoseGapOverrideModal />

			{handoverResponse && (
				<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" role="alertdialog" aria-modal="true">
					<div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
						<div className={`mb-3 inline-flex rounded-full px-3 py-1 text-sm font-semibold ${handoverResponse.status === 'accepted'
							? 'bg-emerald-50 text-emerald-700'
							: 'bg-red-50 text-red-700'}`}>
							{handoverResponse.status === 'accepted' ? 'Handover accepted' : 'Handover declined'}
						</div>
						<h2 className="text-lg font-semibold text-slate-800">
							{handoverResponse.recipientName} {handoverResponse.status === 'accepted' ? 'accepted the handover.' : 'declined the handover.'}
						</h2>
						<p className="mt-2 text-sm text-slate-600">
							You can now see the outcome of the medication handover request.
						</p>
						<button
							type="button"
							onClick={() => setHandoverResponse(null)}
							className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white"
						>
							Okay
						</button>
					</div>
				</div>
			)}

			{updateAvailable && (
				<div className="fixed bottom-0 left-0 right-0 bg-yellow-300 text-black p-3 text-center z-50">
					New version available. Please refresh.
					<button className="ml-2 underline" onClick={reloadPage}>Reload</button>
				</div>
			)}

			{!isOnline && (
				<div className="fixed inset-0 flex items-center justify-center z-50 backdrop-filter backdrop-blur-sm">
					<div className="bg-white p-6 rounded-lg shadow-lg">
						<div className="flex items-center mb-4">
							<svg className="w-6 h-6 mr-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
							<p className="font-semibold text-lg">Connection Lost</p>
						</div>
						<p>Please check your internet connection.</p>
					</div>
				</div>)}


			<AppRoutes />

		</>
	);
}

export default App;
